<template>
  <div id="batch">
    <div class="atb-flex atb-flex-row atb-gap-4 atb-pt-6 atb-px-4 atb-pb-4">
      <div class="atb-flex-grow">
        <h2 class="atb-text-xl atb-font-semibold atb-mb-4">Last 10 Jobs</h2>
        <div class="eds-table-wrap atb-w-full atb-h-full">
          <Toolbar
            class-name="atb-bg-white atb-border-t-0 atb-border-x-0 atb-border-b atb-border-solid atb-border-gray-300"
          >
            <template #toolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="batchListRefreshButton"
                    motif="tertiary"
                    :title="$t('refresh')"
                    aria-label="refresh button"
                    @click="refresh"
                  >
                    <template #icon>
                      <span class="material-icons">loop</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
              <ToolbarSection>
                <template #sectionContent>
                  <Button
                    id="batchListCreateButton"
                    motif="tertiary"
                    :title="$t('create')"
                    aria-label="create button"
                    @click="openCreateForm"
                  >
                    <template #icon>
                      <span class="material-icons">add_circle_outline</span>
                    </template>
                  </Button>
                </template>
              </ToolbarSection>
            </template>
          </Toolbar>
          <AgGridVue
            ref="batchListRef"
            id="batchListAllUploadsGrid"
            :style="{ height: '500px' }"
            class="ag-theme-eds atb-w-full atb-h-full"
            :row-data="metadataHistory"
            :column-defs="columnDefs"
            :suppress-cell-selection="true"
            :suppress-row-click-selection="true"
            :pagination="true"
            :pagination-page-size="20"
            @grid-ready="onGridReady"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Toolbar from '@atb-ui/eds-vue/src/components/Toolbar/Toolbar.vue';
  import ToolbarSection from '@atb-ui/eds-vue/src/components/Toolbar/ToolbarSection.vue';
  import { useBatchStore } from '@/app/features/admin/batch/batch-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { GridApi, GridReadyEvent } from 'ag-grid-community';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import { storeToRefs } from 'pinia';
  import { Ref, ref, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { ListEvents } from '@/app/shared/types/list-events.interface';

  interface BatchListEvents extends ListEvents<any> {}

  const emit = defineEmits<BatchListEvents>();
  const { t } = useI18n();
  const batchStore = useBatchStore();
  const { metadataHistory } = storeToRefs(batchStore);
  const alertStore = useAlertStore();
  const gridApi = ref(null) as Ref<GridApi | null>;
  const batchListRef = ref(null) as Ref<any>;

  // Fetch metadata history when component mounts
  onMounted(async () => {
    await batchStore.fetchMetadataHistory();
  });

  const columnDefs = [
    {
      field: 'systemName',
      headerName: 'System Name',
      suppressMovable: true,
      width: 200,
      minWidth: 200,
      sortable: true,
      resizable: true,
    },
    {
      field: 'dataSource',
      headerName: 'Data Source',
      suppressMovable: true,
      width: 200,
      minWidth: 200,
      sortable: true,
      resizable: true,
    },
    {
      field: 'deploymentFileName',
      headerName: 'Deployment File',
      suppressMovable: true,
      width: 200,
      minWidth: 200,
      sortable: true,
      resizable: true,
    },
    {
      field: 's3InputKey',
      headerName: 'Input Data',
      suppressMovable: true,
      width: 250,
      minWidth: 250,
      sortable: true,
      resizable: true,
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        const key = String(params.value);
        const idx = key.lastIndexOf('/');
        return idx >= 0 ? key.substring(idx + 1) : key;
      },
    },
    {
      field: 'lastModified',
      headerName: 'Run Date',
      suppressMovable: true,
      width: 200,
      minWidth: 200,
      sortable: true,
      resizable: true,
      valueFormatter: (params: any) => {
        if (params.value) {
          return new Date(params.value).toLocaleString();
        }
        return '';
      },
    },
  ];

  const onGridReady = (params: GridReadyEvent) => {
    gridApi.value = params.api;
    gridApi.value.sizeColumnsToFit();
  };

  const refresh = async (): Promise<void> => {
    await batchStore.fetchMetadataHistory();
    gridApi.value?.deselectAll();
    gridApi.value?.redrawRows();
  };

  const openCreateForm = () => {
    emit('create');
  };

  defineExpose({
    refresh,
    gridApi,
  });
</script>

<style scoped>
  .icon {
    width: 20px;
  }
</style>
